import dayjs from "dayjs";

export const GRID_STORAGE_KEY = "doc-plat-grid-v4";
export const FILTERS_STORAGE_KEY = "doc-plat-filters";

export const VID_PLAT_META = {
  1: { label: "Приход", color: "green" },
  [-1]: { label: "Расход / Возврат", color: "red" },
  2: { label: "Пополнение депозита", color: "blue" },
  [-2]: { label: "Возврат депозита", color: "orange" },
  3: { label: "Внесение в кассу", color: "cyan" },
  [-3]: { label: "Выплата из кассы", color: "volcano" },
  4: { label: "Оплата залога", color: "geekblue" },
  [-4]: { label: "Возврат залога", color: "magenta" }
};

export const VID_PLAT_OPTIONS = [
  { value: 0, label: "Все типы" },
  { value: 1, label: "Приход" },
  { value: -1, label: "Расход / Возврат" },
  { value: 2, label: "Пополнение депозита" },
  { value: -2, label: "Возврат депозита" },
  { value: 3, label: "Внесение в кассу" },
  { value: -3, label: "Выплата из кассы" },
  { value: 4, label: "Оплата залога" },
  { value: -4, label: "Возврат залога" }
];

export const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj?.[k] != null) return obj[k];
  }
  return undefined;
};

export const toNumber = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const toFlag = (v) => {
  const n = toNumber(v);
  return n === 1 || v === true;
};

export const getLawFlag = (law, index) => {
  if (!law || law.length <= index) return 1;
  const n = Number(law[index]);
  return Number.isFinite(n) ? n : 1;
};

export const formatDate = (value) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD.MM.YYYY") : "";
};

export const formatMoney = (value) => {
  const n = toNumber(value);
  if (n == null) return "";
  return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatMoneyRub = (value) => {
  const formatted = formatMoney(value);
  return formatted ? `${formatted} ₽` : "";
};

const TABLE_FONT = `400 13px Manrope, "Segoe UI", Tahoma, sans-serif`;
const TABLE_HEADER_FONT = `500 12px Manrope, "Segoe UI", Tahoma, sans-serif`;
const BADGE_FONT = `400 11px Manrope, "Segoe UI", Tahoma, sans-serif`;

let measureCtx;

const getMeasureCtx = () => {
  if (typeof document === "undefined") return null;
  if (!measureCtx) {
    measureCtx = document.createElement("canvas").getContext("2d");
  }
  return measureCtx;
};

/** Ширина колонки по заголовку и ячейкам, не меньше min. */
export const measureContentColWidth = (header, values, { min = 80, pad = 20, bodyFont } = {}) => {
  const ctx = getMeasureCtx();
  if (!ctx) return min;
  ctx.font = TABLE_HEADER_FONT;
  let max = ctx.measureText(String(header || "")).width;
  ctx.font = bodyFont || TABLE_FONT;
  for (const value of values) {
    const w = ctx.measureText(String(value ?? "")).width;
    if (w > max) max = w;
  }
  return Math.max(min, Math.ceil(max + pad));
};

export const measurePlatAutoColWidths = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return {
    vid: measureContentColWidth(
      "Вид",
      list.map((r) => VID_PLAT_META[r.vid]?.label || `Вид ${r.vid ?? ""}`),
      { min: 88, pad: 28, bodyFont: BADGE_FONT }
    ),
    symaExp: measureContentColWidth(
      "Сумма",
      list.map((r) => formatMoneyRub(r.symaExp)),
      { min: 88, pad: 24 }
    ),
    predName: Math.min(
      400,
      measureContentColWidth(
        "Контрагент",
        list.map((r) => r.predName),
        { min: 100, pad: 24 }
      )
    )
  };
};

export const calcSymaExp = (syma, qty, prcDiscount) => {
  const s = Number(syma) || 0;
  const q = Number(qty) || 1;
  const p = Number(prcDiscount) || 0;
  return Math.round(s * q * (1 - p / 100) * 100) / 100;
};

export const defaultPeriod = () => {
  const y = dayjs().year();
  return {
    dateBeg: dayjs(`${y}-01-01`).format("YYYY-MM-DD"),
    dateEnd: dayjs(`${y}-12-31`).format("YYYY-MM-DD"),
    sortBy: "fdate",
    sortDir: "desc",
    showDeleted: false,
    idKassa: 0,
    vid: 0,
    q: ""
  };
};

const isIsoDate = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) && dayjs(value).isValid();

const FILTER_URL_KEYS = [
  "dateBeg",
  "dateEnd",
  "q",
  "idKassa",
  "vid",
  "showDeleted",
  "sortBy",
  "sortDir"
];

const normalizeFiltersShape = (parsed, base = defaultPeriod()) => {
  const sortBy =
    typeof parsed.sortBy === "string" && parsed.sortBy.trim()
      ? parsed.sortBy.trim()
      : base.sortBy;
  const sortDir = parsed.sortDir === "asc" ? "asc" : "desc";
  const idKassa = toNumber(parsed.idKassa) || 0;
  const vid = toNumber(parsed.vid) || 0;
  const showDeleted =
    parsed.showDeleted === true ||
    parsed.showDeleted === 1 ||
    parsed.showDeleted === "1" ||
    parsed.showDeleted === "true";
  return {
    dateBeg: isIsoDate(parsed.dateBeg) ? parsed.dateBeg : base.dateBeg,
    dateEnd: isIsoDate(parsed.dateEnd) ? parsed.dateEnd : base.dateEnd,
    sortBy,
    sortDir,
    showDeleted,
    idKassa,
    vid,
    q: typeof parsed.q === "string" ? parsed.q : ""
  };
};

export const filtersToSearchParams = (filters) => {
  const params = new URLSearchParams();
  const base = defaultPeriod();
  if (isIsoDate(filters?.dateBeg) && filters.dateBeg !== base.dateBeg) {
    params.set("dateBeg", filters.dateBeg);
  }
  if (isIsoDate(filters?.dateEnd) && filters.dateEnd !== base.dateEnd) {
    params.set("dateEnd", filters.dateEnd);
  }
  const q = typeof filters?.q === "string" ? filters.q.trim() : "";
  if (q) params.set("q", q);
  if (Number(filters?.idKassa) > 0) params.set("idKassa", String(Number(filters.idKassa)));
  if (Number(filters?.vid)) params.set("vid", String(Number(filters.vid)));
  if (filters?.showDeleted) params.set("showDeleted", "1");
  if (typeof filters?.sortBy === "string" && filters.sortBy && filters.sortBy !== "fdate") {
    params.set("sortBy", filters.sortBy);
  }
  if (filters?.sortDir === "asc") params.set("sortDir", "asc");
  return params;
};

export const filtersFromSearchParams = (searchParams, fallback) => {
  const params =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(searchParams || "");
  const hasAny = FILTER_URL_KEYS.some((key) => params.has(key));
  if (!hasAny) return null;

  const base = { ...defaultPeriod(), ...(fallback || {}) };

  return normalizeFiltersShape(
    {
      dateBeg: params.has("dateBeg") ? params.get("dateBeg") : base.dateBeg,
      dateEnd: params.has("dateEnd") ? params.get("dateEnd") : base.dateEnd,
      q: params.has("q") ? params.get("q") ?? "" : "",
      idKassa: params.has("idKassa") ? params.get("idKassa") : 0,
      vid: params.has("vid") ? params.get("vid") : 0,
      showDeleted: params.has("showDeleted") ? params.get("showDeleted") : false,
      sortBy: params.has("sortBy") ? params.get("sortBy") : "fdate",
      sortDir: params.has("sortDir") ? params.get("sortDir") : "desc"
    },
    base
  );
};

export const filtersSearchEquals = (a, b) =>
  filtersToSearchParams(a).toString() === filtersToSearchParams(b).toString();

export const loadStoredFilters = () => defaultPeriod();

export const saveFilters = () => {
  try {
    localStorage.removeItem(FILTERS_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

export const normalizeListRow = (row) => {
  const idReg = toNumber(pick(row, "idReg", "IdReg"));
  return {
    idReg,
    key: idReg != null ? String(idReg) : undefined,
    docnum: toNumber(pick(row, "docnum", "Docnum")) ?? 0,
    fdate: pick(row, "fdate", "Fdate") ?? null,
    idKassa: toNumber(pick(row, "idKassa", "IdKassa")) ?? 0,
    kassaName: pick(row, "kassaName", "KassaName") ?? "",
    syma: toNumber(pick(row, "syma", "Syma")) ?? 0,
    qty: toNumber(pick(row, "qty", "Qty")) ?? 1,
    prcDiscount: toNumber(pick(row, "prcDiscount", "PrcDiscount")) ?? 0,
    symaExp: toNumber(pick(row, "symaExp", "SymaExp")) ?? 0,
    vid: toNumber(pick(row, "vid", "Vid")) ?? 0,
    komment: pick(row, "komment", "Komment") ?? "",
    txtOsn: pick(row, "txtOsn", "TxtOsn") ?? "",
    idText: toNumber(pick(row, "idText", "IdText")) ?? 0,
    idClient: toNumber(pick(row, "idClient", "IdClient")) ?? 0,
    predName: pick(row, "predName", "PredName") ?? "",
    idTov: toNumber(pick(row, "idTov", "IdTov")) ?? 0,
    tovName: pick(row, "tovName", "TovName") ?? "",
    idOsn: toNumber(pick(row, "idOsn", "IdOsn")) ?? 0,
    vidOsn: toNumber(pick(row, "vidOsn", "VidOsn")) ?? 0,
    isDel: toFlag(pick(row, "isDel", "IsDel")),
    idCard: toNumber(pick(row, "idCard", "IdCard")) ?? 0,
    keyCardShort: pick(row, "keyCardShort", "KeyCardShort") ?? "",
    dateUse: pick(row, "dateUse", "DateUse") ?? null,
    avtorName: pick(row, "avtorName", "AvtorName") ?? "",
    idCheck: toNumber(pick(row, "idCheck", "IdCheck")) ?? 0,
    idSkl: toNumber(pick(row, "idSkl", "IdSkl")) ?? 0,
    sklName: pick(row, "sklName", "SklName") ?? "",
    mark: pick(row, "mark", "Mark") ?? "",
    hsReqId: pick(row, "hsReqId", "HsReqId") ?? "",
    hsReqTimestamp: pick(row, "hsReqTimestamp", "HsReqTimestamp") ?? ""
  };
};

export const mapLookupItems = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((row) => ({
      value: toNumber(pick(row, "id", "Id", "value", "Value")),
      label: pick(row, "name", "Name", "label", "Label") ?? ""
    }))
    .filter((o) => o.value != null);

export const normalizeLookups = (data) => ({
  vids: mapLookupItems(pick(data, "vids", "Vids")),
  kassas: mapLookupItems(pick(data, "kassas", "Kassas")),
  sklads: [{ value: 0, label: "—" }, ...mapLookupItems(pick(data, "sklads", "Sklads"))],
  tovs: [{ value: 0, label: "—" }, ...mapLookupItems(pick(data, "tovs", "Tovs"))],
  txtOsns: mapLookupItems(pick(data, "txtOsns", "TxtOsns")),
  defaultIdKassa: toNumber(pick(data, "defaultIdKassa", "DefaultIdKassa")) ?? 0
});
